import { login, updatePassword, logout, getInfo } from '@/api/user'
import {
  getAccessToken,
  setAccessToken,
  removeAccessToken,
  getTenantId,
  setTenantId,
  removeTenantId
} from '@/utils/localStorage'
import router, { resetRouter } from '@/router'

const state = {
  token: getAccessToken(),
  USERNAME: '',
  TENANT_ID: getTenantId() || '',
  TENANT_NAME: '',
  name: '',
  avatar: '',
  introduction: '',
  roles: []
}

const mutations = {
  SET_TOKEN: (state, token) => {
    state.token = token
  },
  SET_USERNAME: (state, USERNAME) => {
    state.USERNAME = USERNAME
  },
  SET_TENANT_ID: (state, TENANT_ID) => {
    state.TENANT_ID = TENANT_ID
  },
  SET_TENANT_NAME: (state, TENANT_NAME) => {
    state.TENANT_NAME = TENANT_NAME
  },
  SET_INTRODUCTION: (state, introduction) => {
    state.introduction = introduction
  },
  SET_NAME: (state, name) => {
    state.name = name
  },
  SET_AVATAR: (state, avatar) => {
    state.avatar = avatar
  },
  SET_ROLES: (state, roles) => {
    state.roles = roles
  }
}

const actions = {
  /**
   * 使用者登入
   * @param {Object} userInfo - 登入資訊 { tenantId, username, password }
   */
  login({ commit }, userInfo) {
    const { tenantId, username, password } = userInfo
    return new Promise((resolve, reject) => {
      login({ tenantId, username: username.trim(), password: password })
        .then((response) => {
          const { data } = response
          // 只存儲 Access Token（Refresh Token 由後端自動設置為 httpOnly Cookie）
          commit('SET_TOKEN', data.accessToken)
          setAccessToken(data.accessToken)
          resolve()
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  /**
   * 取得使用者資訊
   * 從 API 取得使用者資料並設定角色權限
   */
  getInfo({ commit, state }) {
    return new Promise((resolve, reject) => {
      getInfo()
        .then((response) => {
          const { data } = response

          if (!data) {
            reject(new Error('驗證失敗，請重新登入'))
            return
          }

          const { USERNAME, TENANT_ID, TENANT_NAME, ROLE } = data
          // 使用 API 回傳的真實角色
          const roles = [ROLE]

          commit('SET_ROLES', roles)
          commit('SET_USERNAME', USERNAME)
          commit('SET_TENANT_ID', TENANT_ID || '')
          commit('SET_TENANT_NAME', TENANT_NAME || '')
          setTenantId(TENANT_ID || '')

          data.roles = roles
          resolve(data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  /**
   * 更新使用者密碼
   * @param {Object} userInfo - 使用者資訊 { PASSWORD_CUR, PASSWORD_NEW }
   */
  updatePassword({ commit }, userInfo) {
    return new Promise((resolve, reject) => {
      updatePassword(userInfo)
        .then((response) => {
          const { data } = response

          if (!data) {
            reject(new Error('驗證失敗，請重新登入'))
            return
          }

          // 只存儲 Access Token（Refresh Token 由後端自動設置為 httpOnly Cookie）
          commit('SET_TOKEN', data.accessToken)
          setAccessToken(data.accessToken)

          resolve(data)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },

  /**
   * 使用者登出
   * 清除 token、角色權限、路由和已訪問的視圖快取
   * 即使後端 API 調用失敗，也會清除前端狀態（用於 token 失效的情況）
   */
  logout({ commit, dispatch }) {
    return new Promise((resolve) => {
      // Refresh Token 會自動通過 Cookie 傳遞，不需要手動傳遞
      // 即使 API 調用失敗（如 token 已失效），也要清除前端狀態
      logout()
        .then(() => {
          // API 成功：清除前端狀態
          commit('SET_TOKEN', '')
          commit('SET_USERNAME', '')
          commit('SET_TENANT_ID', '')
          commit('SET_TENANT_NAME', '')
          commit('SET_ROLES', [])
          removeAccessToken()
          removeTenantId()
          resetRouter()

          // 清除已訪問的視圖快取
          dispatch('tagsView/delAllViews', null, { root: true })

          resolve()
        })
        .catch(() => {
          // API 失敗（如 token 已失效）：仍然清除前端狀態
          commit('SET_TOKEN', '')
          commit('SET_USERNAME', '')
          commit('SET_TENANT_ID', '')
          commit('SET_TENANT_NAME', '')
          commit('SET_ROLES', [])
          removeAccessToken()
          removeTenantId()
          resetRouter()

          // 清除已訪問的視圖快取
          dispatch('tagsView/delAllViews', null, { root: true })

          resolve()
        })
    })
  },

  /**
   * 動態修改權限
   * 重新取得使用者資訊並根據角色生成可訪問的路由
   * @param {string} role - 角色（目前未使用，保留以備未來擴展）
   */
  async changeRoles({ commit, dispatch }, role) {
    const { roles } = await dispatch('getInfo')

    resetRouter()

    // 根據角色生成可訪問的路由
    const accessRoutes = await dispatch('permission/generateRoutes', roles, { root: true })
    // 動態新增可訪問的路由
    router.addRoutes(accessRoutes)

    // 清除已訪問的視圖快取
    dispatch('tagsView/delAllViews', null, { root: true })
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
